import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../../lib/api';
import { mergeDraftItems } from '../../lib/drafts';
import { useAuth } from '../../contexts/AuthContext';
import ProfessionalLayout from '../../components/ProfessionalLayout';
import '../../styles/professional-theme.css';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
// Select components removed - status updates handled by admin only
import { Badge } from '../../components/ui/badge';
import { Plus, Package as PackageIcon, Ship, QrCode, Search, Printer, Camera, ImageIcon, Plane, Clock, TrendingUp, CheckCircle, Pencil, Trash2 } from 'lucide-react';
import { TRANSPORT_MODE_LABELS, TRANSPORT_MODE_COLORS, STATUS_LABELS } from '../../lib/constants';
import { useLanguage } from '../../contexts/LanguageContext';
import SEOHead from '../../components/SEOHead';
import PackageStatusBadge from '../../components/PackageStatusBadge';
import { CountryRoute } from '../../components/CountryDisplay';
import PackagePhotosDialog from '../../components/PackagePhotosDialog';
import EmptyState from '../../components/EmptyState';
import { printPackageLabel } from '../../lib/packageLabel';
import Pagination from '../../components/Pagination';
import PackageFormShell from '../../components/package-form/PackageFormShell';
import {
  STEP_IDS, createEmptyForm, packageToForm, packagesToForm, usePackageForm,
} from '../../components/package-form/usePackageForm';
import { buildPackagePayloads } from '../../components/package-form/payload';
import { suggestedItemPrice } from '../../components/package-form/pricing';
import NotificationAudit from '../../components/NotificationAudit';
import WhatsAppIcon from '../../components/icons/WhatsAppIcon';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/** Extract a displayable error string from FastAPI/axios errors */
const extractErrorMessage = (err, fallback = 'Erreur inconnue') => {
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.userMessage || err?.message || fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(d => d.msg || JSON.stringify(d)).join(', ');
  return JSON.stringify(detail);
};

const DELETE_REASONS = {
  duplicate: 'Doublon — enregistré plusieurs fois',
  client_request: 'Demande du client — annulation',
  incorrect_data: 'Données incorrectes — erreur de saisie',
  refused_package: 'Colis refusé — conditions non respectées',
  damaged_package: 'Colis endommagé — non transportable',
  regulatory_issue: 'Problème réglementaire — contenu interdit',
  other: 'Autre motif — à préciser',
};
const DELETE_BLOCKED_STATUSES = ['in_transit', 'arrived', 'customs_cleared', 'delivered'];

const PartnerDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const canDeletePackages = ['company_admin', 'country_admin', 'super_admin'].includes(user?.role);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, pkg: null });
  const [deleteReason, setDeleteReason] = useState('duplicate');
  const [deleteCustom, setDeleteCustom] = useState('');
  const [editingPackage, setEditingPackage] = useState(null); // { packageId, oce_code }
  const [deleting, setDeleting] = useState(false);
  const { t, language } = useLanguage();
  const [packages, setPackages] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Formulaire de colis partagé (création, édition, brouillon QR).
  const formApi = usePackageForm({
    variant: 'partner',
    initialForm: createEmptyForm({ originCountry: user?.assigned_country || '' }),
  });
  const { form } = formApi;

  // États pour la tarification auto
  const [routePricing, setRoutePricing] = useState(null);
  const [merchandiseCategories, setMerchandiseCategories] = useState([]);
  const [pricingInfo, setPricingInfo] = useState(null);

  // États pour QR Code
  const [showQRDialog, setShowQRDialog] = useState(false);
  const [qrSession, setQRSession] = useState(null);
  const [pendingQRSubmissions, setPendingQRSubmissions] = useState([]);
  const [draftPackages, setDraftPackages] = useState([]);
  const [editingDraft, setEditingDraft] = useState(null); // { sessionId, oce_code }

  // État pour les photos de colis déjà enregistrées
  const [photosDialog, setPhotosDialog] = useState({ open: false, packageId: null, oceCode: '' });

  // États pour le mode de transport
  const [transportModes, setTransportModes] = useState(['aerien']);
  const [defaultTransportMode, setDefaultTransportMode] = useState('aerien');

  // État nom entreprise
  const [companyName, setCompanyName] = useState('');
  const [companyLogo, setCompanyLogo] = useState('');
  const [usage, setUsage] = useState(null);

  // Charte d'acceptation (portée par le formulaire partagé)
  const allCharterAccepted = form.charter.inspectionConsent
    && form.charter.termsAccepted
    && form.charter.noProhibitedItems;

  // États pour la pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [statusFilter, setStatusFilter] = useState('all');
  const isDraftsPage = location.pathname === '/my-packages/drafts';

  // Déclarations QR et colis détachés d'un départ : un seul écran de brouillons.
  const draftItems = useMemo(
    () => mergeDraftItems(pendingQRSubmissions, draftPackages),
    [pendingQRSubmissions, draftPackages],
  );

  useEffect(() => {
    fetchPackages();
    fetchCountries();
    fetchPendingQRSubmissions();
    fetchMerchandiseCategories();
    fetchTransportModes();
    fetchCompanyName();
    fetchUsage();
    fetchDraftPackages();
  }, []);

  const fetchDraftPackages = async () => {
    try {
      const res = await api.get('/api/packages?status=draft');
      setDraftPackages((res.data || []).filter(p => p.status === 'draft'));
    } catch (_) { /* non-blocking */ }
  };

  // Un colis détaché d'un départ porte déjà sa facture : on le remet en
  // circulation sans repasser par la validation, qui en émettrait une seconde.
  const handleValidatePackageDraft = async (item) => {
    try {
      await api.put(`/api/packages/${item.packageId}/restore-to-registered`);
      setSuccess(`Colis ${item.oce_code} validé.`);
      fetchDraftPackages();
      fetchPackages();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur');
    }
  };

  const fetchCompanyName = async () => {
    try {
      const res = await api.get(`${API}/company/settings`);
      setCompanyName(res.data?.general?.company_name || '');
      setCompanyLogo(res.data?.general?.logo_url || '');
    } catch (_) { /* non-blocking */ }
  };

  const fetchUsage = async () => {
    try {
      const res = await api.get('/api/subscriptions/usage');
      setUsage(res.data);
    } catch (_) { /* non-blocking */ }
  };

  // Auto-refresh quand un client soumet via QR
  useEffect(() => {
    const handleQRSubmission = () => {
      fetchPackages();
      fetchPendingQRSubmissions();
    };
    window.addEventListener('qr_submission_received', handleQRSubmission);
    return () => window.removeEventListener('qr_submission_received', handleQRSubmission);
  }, []);

  // Initialiser le pays d'origine automatiquement quand l'utilisateur est chargé
  useEffect(() => {
    if (user?.assigned_country) {
      formApi.setField('origin_country', user.assigned_country);
    }
  }, [user]);

  const fetchPackages = async () => {
    try {
      const response = await api.get(`${API}/packages?exclude_shipped=true`);
      const isAdmin = user?.role === 'company_admin' || user?.role === 'super_admin';
      const filteredPackages = isAdmin ? response.data : response.data.filter(pkg => pkg.status !== 'collected');
      setPackages(filteredPackages);
    } catch (err) {
      console.error('Error fetching packages:', err);
    }
  };

  const fetchCountries = async () => {
    try {
      const response = await api.get(`${API}/countries`);
      setCountries(response.data);
    } catch (err) {
      console.error('Error fetching countries:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingQRSubmissions = async () => {
    try {
      const response = await api.get(`${API}/qr-sessions/pending`);
      setPendingQRSubmissions(response.data);
    } catch (err) {
      console.error('Error fetching pending QR submissions:', err);
    }
  };

  const fetchMerchandiseCategories = async () => {
    try {
      const response = await api.get(`${API}/company/pricing/categories`);
      setMerchandiseCategories(response.data);
    } catch (err) {
      console.error('Error fetching merchandise categories:', err);
    }
  };

  const fetchTransportModes = async () => {
    try {
      const response = await api.get(`${API}/company/transport-modes`);
      const modes = response.data.transport_modes || ['aerien'];
      const defaultMode = response.data.default_transport_mode || 'aerien';
      setTransportModes(modes);
      setDefaultTransportMode(defaultMode);
      formApi.setField('transport_mode', defaultMode);
    } catch (err) {
      console.error('Error fetching transport modes:', err);
    }
  };

  // Tarif de la route dès que origine + destination sont connues
  useEffect(() => {
    const fetchRoutePricing = async () => {
      if (!form.origin_country || !form.destination_country) {
        setRoutePricing(null);
        setPricingInfo(null);
        return;
      }
      try {
        const res = await api.get(
          `${API}/company/pricing/route?origin=${encodeURIComponent(form.origin_country)}&destination=${encodeURIComponent(form.destination_country)}`
        );
        setRoutePricing(res.data);
      } catch {
        setRoutePricing(null);
      }
    };
    fetchRoutePricing();
  }, [form.origin_country, form.destination_country]);

  // Tarif de la route : chaque colis reçoit sa propre suggestion de prix.
  // Le supplément de catégorie ne s'applique qu'au premier colis de l'envoi.
  useEffect(() => {
    if (!routePricing?.found) {
      setPricingInfo(routePricing ? { found: false } : null);
      return;
    }
    const supplement = form.merchandise_category_id
      ? merchandiseCategories.find(c => c.id === form.merchandise_category_id)?.fixed_price || 0
      : 0;
    setPricingInfo({
      found: true,
      ratePerKg: routePricing.rate_per_kg,
      currency: routePricing.currency,
      supplement,
      suggest: (item, index) => suggestedItemPrice({
        item,
        transportMode: form.transport_mode,
        ratePerKg: routePricing.rate_per_kg,
        supplement: index === 0 ? supplement : 0,
      }),
    });
  }, [routePricing, form.merchandise_category_id, form.transport_mode, merchandiseCategories]);

  // Un prix laissé vide prend la valeur du tarif ; une saisie manuelle n'est jamais écrasée.
  useEffect(() => {
    if (!pricingInfo?.found) return;
    form.items.forEach((item, index) => {
      if (item.price !== '') return;
      const suggested = pricingInfo.suggest(item, index);
      if (suggested > 0) formApi.setItem(index, 'price', String(suggested));
    });
  }, [pricingInfo, form.items]);

  const createQRSession = async () => {
    try {
      const response = await api.post(`${API}/qr-sessions/create`, {});
      setQRSession(response.data);
      setShowQRDialog(true);
      setSuccess('Session QR créée ! Partagez le lien avec votre client.');
    } catch (err) {
      setError(extractErrorMessage(err, t('errorCreating')));
    }
  };

  const printLabel = (pkg) => printPackageLabel(pkg, { logoUrl: companyLogo, companyName: companyName || 'FretManager' });

  const handleDeletePackage = async () => {
    if (!deleteDialog.pkg) return;
    if (deleteReason === 'other' && !deleteCustom.trim()) return;
    setDeleting(true);
    try {
      await api.delete(`/api/packages/${deleteDialog.pkg.id}`, {
        data: { deletion_reason: deleteReason, custom_reason: deleteReason === 'other' ? deleteCustom.trim() : null },
      });
      setDeleteDialog({ open: false, pkg: null });
      setDeleteReason('duplicate');
      setDeleteCustom('');
      fetchPackages();
    } catch (err) {
      alert(err.response?.data?.detail || 'Erreur lors de la suppression');
    } finally {
      setDeleting(false);
    }
  };

  // Ouvre le grand formulaire (identique à la création/brouillon), pré-rempli,
  // en mode « édition d'un colis existant ».
  // Ouvre le grand formulaire (identique à la création/brouillon), pré-rempli,
  // en mode « édition d'un colis existant ».
  const openEditDialog = (pkg) => {
    formApi.reset(packageToForm(pkg, {
      transportMode: defaultTransportMode,
      originCountry: user?.assigned_country || '',
    }));
    setEditingDraft(null);
    setEditingPackage({ packageId: pkg.id, oce_code: pkg.oce_code || '' });
    setOpenDialog(true);
  };

  const handleUpdatePackage = async (e) => {
    if (e) e.preventDefault();
    if (!editingPackage || submitting) return;
    setError('');
    setSuccess('');

    if (!STEP_IDS.every((id) => formApi.validateStep(id))) {
      setError(language === 'en' ? 'Some fields are missing.' : 'Des champs sont manquants.');
      return;
    }

    setSubmitting(true);
    try {
      // Champs éditables uniquement (PackageEdit) — prix/paiement/statut exclus côté backend.
      const isMaritime = form.transport_mode === 'maritime';
      const item = form.items[0];
      const payload = {
        sender_name: form.sender.name || undefined,
        sender_phone: form.sender.phone || undefined,
        sender_email: form.sender.email || undefined,
        recipient_name: form.recipient.name || undefined,
        recipient_phone: form.recipient.phone || undefined,
        recipient_email: form.recipient.email || undefined,
        weight: !isMaritime && item.weight ? parseFloat(item.weight) : undefined,
        description: item.description || undefined,
        destination_country: form.destination_country || undefined,
        destination_city: form.destination_city || undefined,
        merchandise_category_id: form.merchandise_category_id || undefined,
        transport_mode: form.transport_mode || undefined,
        length_cm: isMaritime && item.length_cm ? parseFloat(item.length_cm) : undefined,
        width_cm: isMaritime && item.width_cm ? parseFloat(item.width_cm) : undefined,
        height_cm: isMaritime && item.height_cm ? parseFloat(item.height_cm) : undefined,
      };
      Object.keys(payload).forEach((k) => payload[k] === undefined && delete payload[k]);

      await api.put(`/api/packages/${editingPackage.packageId}`, payload);

      // Upload des éventuelles nouvelles photos (best-effort).
      const photos = item.photos || [];
      if (photos.length > 0) {
        await Promise.allSettled(
          photos.map(({ file }) => {
            const formData = new FormData();
            formData.append('file', file);
            return api.post(`${API}/packages/${editingPackage.packageId}/attachments?file_type=image`, formData, {
              headers: { 'Content-Type': 'multipart/form-data' },
            });
          })
        );
      }

      setSuccess(`Colis ${editingPackage.oce_code} modifié. Le client sera informé de la mise à jour.`);
      setOpenDialog(false);
      setEditingPackage(null);
      fetchPackages();
    } catch (err) {
      setError(extractErrorMessage(err, 'Erreur lors de la modification'));
    } finally {
      setSubmitting(false);
    }
  };

  const validateQRSubmission = async (sessionId) => {
    try {
      const response = await api.post(`${API}/qr-sessions/${sessionId}/validate`, {});
      setSuccess(`Colis ${response.data.oce_code} validé avec succès !`);
      fetchPackages();
      fetchPendingQRSubmissions();
    } catch (err) {
      setError(extractErrorMessage(err, t('errorStatusChange')));
    }
  };

  const openDraftFromPackage = (pkg) => {
    formApi.reset(packageToForm(pkg, {
      transportMode: defaultTransportMode,
      originCountry: user?.assigned_country || '',
    }));
    setEditingPackage(null);
    setEditingDraft({ sessionId: pkg.qr_session_id, packageId: pkg.id, oce_code: pkg.oce_code || '' });
    setOpenDialog(true);
  };

  const openDraftEditor = (submission) => {
    // Une déclaration peut contenir plusieurs colis : on les ouvre tous.
    const drafts = submission.draft_packages?.length
      ? submission.draft_packages
      : [submission.draft_package || submission.client_data || {}];
    const [first] = drafts;

    formApi.reset(packagesToForm(drafts, {
      transportMode: first.transport_mode || defaultTransportMode,
      originCountry: first.origin_country || user?.assigned_country || '',
    }));
    setEditingPackage(null);
    setEditingDraft({
      sessionId: submission.session_id,
      packageIds: drafts.map(d => d.id).filter(Boolean),
      oce_code: first.oce_code || '',
      count: drafts.length,
    });
    setOpenDialog(true);
  };

  const buildDraftPayload = (item) => {
    const isMaritime = form.transport_mode === 'maritime';
    return {
      sender_name: form.sender.name || undefined,
      sender_phone: form.sender.phone || undefined,
      sender_email: form.sender.email || undefined,
      recipient_name: form.recipient.name || undefined,
      recipient_phone: form.recipient.phone || undefined,
      recipient_email: form.recipient.email || undefined,
      weight: !isMaritime && item.weight ? parseFloat(item.weight) : undefined,
      description: item.description || undefined,
      destination_country: form.destination_country || undefined,
      destination_city: form.destination_city || undefined,
      transport_mode: form.transport_mode || undefined,
      length_cm: isMaritime && item.length_cm ? parseFloat(item.length_cm) : undefined,
      width_cm: isMaritime && item.width_cm ? parseFloat(item.width_cm) : undefined,
      height_cm: isMaritime && item.height_cm ? parseFloat(item.height_cm) : undefined,
      price: item.price ? parseFloat(item.price) : undefined,
      payment_status: item.payment_status || undefined,
      payment_method: item.payment_method || undefined,
      advance_amount: item.payment_status === 'advance'
        ? parseFloat(item.advance_amount) || undefined
        : undefined,
    };
  };

  // Chaque colis a son propre brouillon : on met à jour chacun par son identifiant.
  const saveDraftUpdate = async () => {
    const ids = editingDraft.packageIds?.length
      ? editingDraft.packageIds
      : [editingDraft.packageId].filter(Boolean);

    if (ids.length) {
      await Promise.all(form.items.map((item, i) => (
        ids[i] ? api.put(`${API}/qr-sessions/${ids[i]}/draft-direct`, buildDraftPayload(item)) : null
      )).filter(Boolean));
      return;
    }
    // Repli : ancienne déclaration sans identifiants de brouillon.
    if (editingDraft.sessionId) {
      await api.put(`${API}/qr-sessions/${editingDraft.sessionId}/draft`, buildDraftPayload(form.items[0]));
    }
  };

  const handleSaveDraft = async () => {
    if (!editingDraft || submitting) return;
    setSubmitting(true);
    setError('');
    try {
      await saveDraftUpdate();
      setSuccess('Brouillon mis à jour !');
      fetchPackages();
      fetchPendingQRSubmissions();
    } catch (err) {
      setError(extractErrorMessage(err, 'Erreur lors de la mise à jour'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleValidateDraft = async () => {
    if (!editingDraft || submitting) return;
    setSubmitting(true);
    setError('');
    try {
      await saveDraftUpdate();
      if (editingDraft.sessionId) {
        const response = await api.post(`${API}/qr-sessions/${editingDraft.sessionId}/validate`, {});
        setSuccess(`Colis ${response.data.oce_code} validé avec succès !`);
      } else if (editingDraft.packageId) {
        await api.post(`${API}/qr-sessions/${editingDraft.packageId}/validate-direct`, {});
        setSuccess(`Colis ${editingDraft.oce_code} validé avec succès !`);
      }
      setOpenDialog(false);
      setEditingDraft(null);
      fetchPackages();
      fetchPendingQRSubmissions();
    } catch (err) {
      setError(extractErrorMessage(err, 'Erreur lors de la validation'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreatePackage = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setError('');
    setSuccess('');

    if (!STEP_IDS.every((id) => formApi.validateStep(id))) {
      setError(language === 'en' ? 'Some fields are missing.' : 'Des champs sont manquants.');
      return;
    }

    setSubmitting(true);
    try {
      const supplement = form.merchandise_category_id
        ? (merchandiseCategories || []).find(c => c.id === form.merchandise_category_id)?.fixed_price || null
        : null;
      // Un colis = un appel : même logique OCE / facture / notification pour chacun.
      const payloads = buildPackagePayloads(form, { merchandiseSupplement: supplement });

      const codes = [];
      let invoiced = 0;
      for (let i = 0; i < payloads.length; i += 1) {
        const response = await api.post(`${API}/packages`, payloads[i]);
        const created = response.data;
        if (created?.oce_code) codes.push(created.oce_code);
        if (created?.invoice_id) invoiced += 1;

        const photos = form.items[i].photos || [];
        if (photos.length > 0 && created?.id) {
          const results = await Promise.allSettled(photos.map(({ file }) => {
            const formData = new FormData();
            formData.append('file', file);
            return api.post(`${API}/packages/${created.id}/attachments?file_type=image`, formData, {
              headers: { 'Content-Type': 'multipart/form-data' },
            });
          }));
          const failed = results.filter(r => r.status === 'rejected').length;
          if (failed > 0) console.warn(`${failed} photo(s) failed to upload`);
        }
      }

      if (codes.length > 1) {
        setSuccess(language === 'en'
          ? `${codes.length} packages created: ${codes.join(', ')}`
          : `${codes.length} colis créés : ${codes.join(', ')}`);
      } else {
        setSuccess(`Colis ${codes[0]} créé avec succès !${invoiced > 0 ? ' Facture auto-générée.' : ''}`);
      }

      formApi.reset(createEmptyForm({
        transportMode: defaultTransportMode,
        originCountry: user?.assigned_country || '',
      }));
      setOpenDialog(false);
      fetchPackages();
    } catch (err) {
      setError(extractErrorMessage(err, t('errorCreating')));
    } finally {
      setSubmitting(false);
    }
  };

  // Les colis d'un même client déposés le même jour se suivent dans la liste et
  // portent leur rang, pour voir d'un coup d'œil combien il en a confiés.
  const clientKey = (pkg) => `${pkg.sender_phone || pkg.sender_name || ''}|${(pkg.created_at || '').slice(0, 10)}`;

  const visiblePackages = useMemo(() => {
    const query = searchQuery.toLowerCase();
    const matching = packages.filter((pkg) => {
      if (pkg.status === 'draft') return false;
      if (statusFilter !== 'all' && pkg.status !== statusFilter) return false;
      if (!searchQuery) return true;
      return (
        pkg.oce_code?.toLowerCase().includes(query)
        || pkg.sender_name?.toLowerCase().includes(query)
        || pkg.recipient_name?.toLowerCase().includes(query)
        || pkg.sender_phone?.includes(query)
        || pkg.recipient_phone?.includes(query)
      );
    });

    const groups = new Map();
    matching.forEach((pkg) => {
      const key = clientKey(pkg);
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(pkg);
    });

    return [...groups.values()].flatMap((group) => group.map((pkg, index) => ({
      ...pkg,
      group_size: group.length,
      group_index: index + 1,
    })));
  }, [packages, statusFilter, searchQuery]);

  // Status updates are now handled by administrators only

  // Boutons de soumission — rendus par le shell à la dernière étape (ou en bas
  // de la vue d'un bloc pour une correction / un brouillon).
  const dialogFooter = (
    <DialogFooter className="gap-2 sm:gap-0">
      {editingPackage ? (
        <Button type="submit" disabled={submitting} className="w-full sm:w-auto">
          {submitting ? 'Enregistrement...' : (language === 'en' ? 'Save changes' : 'Enregistrer les modifications')}
        </Button>
      ) : editingDraft ? (
        <div className="flex flex-col-reverse sm:flex-row gap-2 w-full sm:justify-end">
          <Button type="button" variant="outline" disabled={submitting} onClick={handleSaveDraft} className="w-full sm:w-auto">
            {submitting ? 'Sauvegarde...' : 'Sauvegarder'}
          </Button>
          <Button type="submit" disabled={submitting || !allCharterAccepted} className="w-full sm:w-auto">
            {submitting ? 'Validation...' : 'Sauvegarder et Valider'}
          </Button>
        </div>
      ) : (
        <Button type="submit" disabled={submitting || !allCharterAccepted} className="w-full sm:w-auto">
          {submitting
            ? t('creating') || 'Création...'
            : form.items.length > 1
              ? (language === 'en' ? `Create the ${form.items.length} packages` : `Créer les ${form.items.length} colis`)
              : t('createPackage')}
        </Button>
      )}
    </DialogFooter>
  );

  if (loading) {
    return (
      <ProfessionalLayout title={language === 'en' ? 'Dashboard - Partner' : 'Tableau de bord - Partenaire'}>
        <p>{t('loading')}</p>
      </ProfessionalLayout>
    );
  }

  return (
    <ProfessionalLayout title={language === 'en' ? 'Dashboard - Partner' : 'Tableau de bord - Partenaire'}>
      <SEOHead title={t('seoDashboardTitle')} description={t('seoDashboardDesc')} noindex />
      <div className="space-y-6">
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        {success && (
          <Alert className="bg-green-500/10 border-border">
            <AlertDescription className="text-green-800">{success}</AlertDescription>
          </Alert>
        )}

        {/* Quota WhatsApp restant pour l'entreprise */}
        {/* Quota affiché seulement quand il devient un risque pour la journée. */}
        {usage && usage.whatsapp_limit !== -1 && (() => {
          const remaining = Math.max(0, (usage.whatsapp_limit || 0) - (usage.whatsapp_this_month || 0));
          const low = usage.whatsapp_limit > 0 && remaining <= usage.whatsapp_limit * 0.1;
          if (!low) return null;
          return (
            <div className="flex items-center gap-2 rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">
              <WhatsAppIcon size={16} className="text-red-600" />
              <span>
                {language === 'en' ? 'Company WhatsApp messages left this month: ' : 'Messages WhatsApp restants ce mois : '}
                <strong>{remaining}</strong> / {usage.whatsapp_limit}
              </span>
              <span className="font-semibold">{language === 'en' ? '— running low' : '— bientôt épuisé'}</span>
            </div>
          );
        })()}

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3">
          <Button onClick={createQRSession} variant="outline" className="w-full sm:w-auto">
            <QrCode className="h-4 w-4 mr-2" />
            {t('createQRCode')}
          </Button>

          <Button onClick={() => setOpenDialog(true)} className="w-full sm:w-auto">
            <Plus className="h-4 w-4 mr-2" />
            {t('createPackage')}
          </Button>

          {/* Barre de recherche */}
          <div className="flex-1 min-w-0 w-full sm:w-auto">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={language === 'en' ? 'Search a package (OCE, name, phone)...' : 'Rechercher un colis (OCE, nom, téléphone)...'}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
        </div>

        {/* Create Package Dialog */}
        <Dialog
          open={openDialog}
          onOpenChange={(open) => {
            setOpenDialog(open);
            if (!open) {
              setEditingDraft(null);
              setEditingPackage(null);
              formApi.reset(createEmptyForm({
                transportMode: defaultTransportMode,
                originCountry: user?.assigned_country || '',
              }));
            }
          }}
        >
          {/* Plein écran sur mobile : un parcours en 4 étapes ne tient pas dans
              une boîte centrée de 90 % de hauteur. Boîte classique dès 640 px. */}
          <DialogContent className="h-[100dvh] max-h-[100dvh] w-screen max-w-none translate-x-[-50%] translate-y-[-50%] overflow-y-auto rounded-none sm:h-auto sm:max-h-[90vh] sm:w-[calc(100%-2rem)] sm:max-w-4xl sm:rounded-lg">
            <form onSubmit={editingPackage ? handleUpdatePackage : editingDraft ? (e) => { e.preventDefault(); handleValidateDraft(); } : handleCreatePackage}>
              <DialogHeader>
                <DialogTitle>
                  {editingPackage
                    ? (language === 'en' ? `Edit package ${editingPackage.oce_code}` : `Modifier le colis ${editingPackage.oce_code}`)
                    : editingDraft
                      ? (language === 'en' ? `Edit draft ${editingDraft.oce_code}` : `Modifier le brouillon ${editingDraft.oce_code}`)
                      : (language === 'en' ? 'Create a new package' : 'Créer un nouveau colis')
                  }
                </DialogTitle>
                <DialogDescription>
                  {editingPackage
                    ? (language === 'en' ? 'Update the information — the client will be notified and the invoice recalculated' : 'Modifiez les informations — le client sera informé et la facture recalculée')
                    : editingDraft
                      ? (language === 'en' ? 'Review and modify the information before validation' : 'Vérifiez et modifiez les informations avant validation')
                      : (language === 'en' ? 'Register a new package in the system' : 'Enregistrez un nouveau colis dans le système')
                  }
                </DialogDescription>
              </DialogHeader>

              <PackageFormShell
                layout={editingPackage || editingDraft ? 'single' : 'wizard'}
                variant="partner"
                api={formApi}
                stepProps={{
                  transportModes,
                  merchandiseCategories,
                  companyName,
                  pricingInfo,
                  hideCharter: Boolean(editingPackage),
                }}
                footer={dialogFooter}
              />
            </form>
          </DialogContent>
        </Dialog>

        {/* Drafts Section */}
        {isDraftsPage && (
          <Card>
            <CardHeader>
              <CardTitle>{language === 'en' ? 'Drafts' : 'Brouillons'} ({draftItems.length})</CardTitle>
              <CardDescription>
                {language === 'en'
                  ? 'Packages awaiting review: edit them, then validate.'
                  : 'Colis en attente de vérification : modifiez-les, puis validez.'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {draftItems.length === 0 ? (
                <EmptyState
                  icon={PackageIcon}
                  title={language === 'en' ? 'No drafts' : 'Aucun brouillon'}
                  description={language === 'en' ? 'Drafts will appear here when a client submits a package via QR code' : 'Les brouillons apparaîtront ici quand un client soumettra un colis via QR code'}
                />
              ) : (
                <div className="space-y-4">
                  {draftItems.map(item => (
                    <div key={item.key} className="border-2 border-orange-200 bg-orange-50/30 rounded-lg p-5">
                      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
                        <div className="space-y-2 flex-1 min-w-0">
                          <div className="flex items-center gap-3">
                            {item.oce_code && <span className="font-mono font-bold text-lg text-orange-700">{item.oce_code}</span>}
                            <PackageStatusBadge status="draft" size="sm" />
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-1 text-sm">
                            <div><span className="text-muted-foreground">{language === 'en' ? 'Sender' : 'Expéditeur'}:</span> <span className="font-medium">{item.sender_name}</span></div>
                            <div><span className="text-muted-foreground">{language === 'en' ? 'Recipient' : 'Destinataire'}:</span> <span className="font-medium">{item.recipient_name}</span></div>
                            <div><span className="text-muted-foreground">{language === 'en' ? 'Weight' : 'Poids'}:</span> <span className="font-medium">{item.weight} kg</span></div>
                            <div><span className="text-muted-foreground">{language === 'en' ? 'Origin' : 'Expédition'}:</span> <span className="font-medium">{item.origin_country || '—'}</span></div>
                            <div><span className="text-muted-foreground">{language === 'en' ? 'Destination' : 'Livraison'}:</span> <span className="font-medium">{item.destination_country}{item.destination_city ? ` / ${item.destination_city}` : ''}</span></div>
                            <div className="sm:col-span-2"><span className="text-muted-foreground">Description:</span> <span className="font-medium">{item.description || '—'}</span></div>
                            <div><span className="text-muted-foreground">{language === 'en' ? 'Payment' : 'Paiement'}:</span> <span className={`font-medium ${item.payment_status === 'paid' ? 'text-green-600' : 'text-red-600'}`}>{item.payment_status === 'paid' ? (language === 'en' ? 'Paid' : 'Payé') : item.payment_status === 'advance' || item.payment_status === 'partial' ? (language === 'en' ? 'Partial' : 'Partiel') : (language === 'en' ? 'Unpaid' : 'Non payé')}</span></div>
                            <div><span className="text-muted-foreground">{language === 'en' ? 'Method' : 'Moyen'}:</span> <span className="font-medium">{item.payment_method || '—'}</span></div>
                            {item.advance_amount > 0 && (
                              <div><span className="text-muted-foreground">{language === 'en' ? 'Advance' : 'Avance'}:</span> <span className="font-medium text-blue-600">{item.advance_amount} €</span></div>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-row sm:flex-col gap-2 sm:ml-4 shrink-0">
                          <Button
                            onClick={() => (item.kind === 'submission'
                              ? openDraftEditor(item.source)
                              : openDraftFromPackage(item.source))}
                            className="flex-1 sm:flex-none text-orange-700 border-orange-300 hover:bg-orange-100"
                            variant="outline"
                          >
                            <Pencil className="h-4 w-4 mr-2" />
                            {language === 'en' ? 'Edit' : 'Modifier'}
                          </Button>
                          <Button
                            className="flex-1 sm:flex-none"
                            onClick={() => (item.kind === 'submission'
                              ? validateQRSubmission(item.sessionId)
                              : handleValidatePackageDraft(item))}
                          >
                            <CheckCircle className="h-4 w-4 mr-2" />
                            {language === 'en' ? 'Validate' : 'Valider'}
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Packages List */}
        {!isDraftsPage && (
        <Card>
          <CardHeader>
            <CardTitle>{t('myPackages')} ({packages.filter(p => p.status !== 'draft').length})</CardTitle>
            <CardDescription>{language === 'en' ? 'List of all your registered packages (sorted newest to oldest)' : 'Liste de tous vos colis enregistrés (triés du plus récent au plus ancien)'}</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            {/* Status filter tabs */}
            <div className="flex gap-1 px-6 pt-4 pb-2 flex-wrap">
              {[
                { key: 'all', label: language === 'en' ? 'All' : 'Tous' },
                { key: 'registered', label: language === 'en' ? 'Registered' : 'Enregistrés' },
                { key: 'shipped', label: language === 'en' ? 'Shipped' : 'Expédiés' },
                { key: 'in_transit', label: language === 'en' ? 'In transit' : 'En transit' },
                { key: 'arrived', label: language === 'en' ? 'Arrived' : 'Arrivés' },
                { key: 'delivered', label: language === 'en' ? 'Delivered' : 'Livrés' },
              ].map(tab => {
                const nonDraftPkgs = packages.filter(p => p.status !== 'draft');
                const count = tab.key === 'all'
                  ? nonDraftPkgs.length
                  : nonDraftPkgs.filter(p => p.status === tab.key).length;
                return (
                  <button
                    key={tab.key}
                    onClick={() => { setStatusFilter(tab.key); setCurrentPage(1); }}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                      statusFilter === tab.key
                        ? 'bg-accent text-white'
                        : 'bg-muted text-muted-foreground hover:bg-muted/80'
                    }`}
                  >
                    {tab.label} {count > 0 && <span className="ml-1 text-xs opacity-75">({count})</span>}
                  </button>
                );
              })}
            </div>
            {packages.length === 0 ? (
              <EmptyState
                icon={PackageIcon}
                title={t('noRegisteredPackages')}
                description={language === 'en' ? 'Start by creating a new package or generating a client QR code' : 'Commencez par créer un nouveau colis ou générer un QR code client'}
                actionLabel={language === 'en' ? 'New package' : 'Nouveau colis'}
                onAction={() => setOpenDialog(true)}
              />
            ) : (
              <>
                <div className="space-y-3 p-6">
                  {visiblePackages
                    .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                    .map(pkg => (
                    <div
                      key={pkg.id}
                      className={`flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 sm:p-4 border rounded-lg hover:bg-muted transition-all gap-2 ${pkg.status === 'draft' ? 'border-orange-300 bg-orange-50/50 cursor-pointer' : ''}`}
                      onClick={() => pkg.status === 'draft' && openDraftFromPackage(pkg)}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <PackageIcon className={`h-5 w-5 shrink-0 ${pkg.status === 'draft' ? 'text-orange-500' : 'text-accent'}`} />
                          <p className="font-medium text-base sm:text-lg truncate">{pkg.oce_code}</p>
                          {pkg.group_size > 1 && (
                            <Badge className="shrink-0 bg-accent/10 text-accent text-xs">
                              {language === 'en'
                                ? `Package ${pkg.group_index} of ${pkg.group_size}`
                                : `Colis ${pkg.group_index} / ${pkg.group_size}`}
                            </Badge>
                          )}
                          <div className="ml-auto shrink-0">
                            {pkg.photo_url ? (
                              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg overflow-hidden bg-muted cursor-pointer"
                                onClick={(e) => { e.stopPropagation(); setPhotosDialog({ open: true, packageId: pkg.id, oceCode: pkg.oce_code }); }}>
                                <img src={pkg.photo_url} alt={pkg.oce_code} className="w-full h-full object-cover" />
                              </div>
                            ) : pkg.photo_count > 0 ? (
                              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-muted flex items-center justify-center cursor-pointer"
                                onClick={(e) => { e.stopPropagation(); setPhotosDialog({ open: true, packageId: pkg.id, oceCode: pkg.oce_code }); }}>
                                <Camera className="h-4 w-4 text-muted-foreground" />
                              </div>
                            ) : null}
                          </div>
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                          {pkg.sender_name} → {pkg.recipient_name}
                        </p>
                        <p className="text-sm text-muted-foreground flex items-center gap-1 flex-wrap">
                          <CountryRoute origin={pkg.origin_country} destination={pkg.destination_country} size="sm" />
                          <span>| {pkg.weight} kg{pkg.price != null ? ` | ${pkg.price.toFixed(2)} €` : ''}</span>
                        </p>
                        <div className="flex items-center gap-2 mt-2">
                          <Badge
                            className={
                              pkg.payment_status === 'paid' ? 'bg-green-100 text-green-800' :
                              pkg.payment_status === 'advance' ? 'bg-orange-100 text-orange-800' :
                              pkg.is_paid ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }
                          >
                            {pkg.payment_status === 'paid' ? t('paid') :
                             pkg.payment_status === 'advance' ? `${language === 'en' ? 'Advance' : 'Avancé'}${pkg.advance_amount ? ` (${pkg.advance_amount}€)` : ''}` :
                             pkg.is_paid ? t('paid') : t('unpaid')}
                          </Badge>
                          {pkg.payment_method && (
                            <Badge className="bg-slate-100 text-slate-700 text-xs">
                              {{ cash: language === 'en' ? 'Cash' : 'Espèces', virement: language === 'en' ? 'Transfer' : 'Virement', mobile_money: 'Mobile Money', carte: language === 'en' ? 'Card' : 'Carte' }[pkg.payment_method] || pkg.payment_method}
                            </Badge>
                          )}
                          {pkg.transport_mode && (
                            <Badge className={`${TRANSPORT_MODE_COLORS[pkg.transport_mode] || 'bg-blue-500 text-white'} text-xs`}>
                              {pkg.transport_mode === 'aerien' ? <Plane className="h-3 w-3 mr-1" /> : <Ship className="h-3 w-3 mr-1" />}
                              {(TRANSPORT_MODE_LABELS[language] || TRANSPORT_MODE_LABELS.fr)[pkg.transport_mode] || pkg.transport_mode}
                            </Badge>
                          )}
                          {pkg.invoice_id && (
                            <Badge className="bg-accent/10 text-accent text-xs">
                              {language === 'en' ? 'Invoiced' : 'Facturé'}
                            </Badge>
                          )}
                          {pkg.shipment_id && (
                            <Badge variant="outline" className="text-xs">
                              {language === 'en' ? 'Assigned to a departure' : 'Assigné à un départ'}
                            </Badge>
                          )}
                        </div>
                      </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0 self-end sm:self-center">
                        {pkg.status === 'draft' ? (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); openDraftFromPackage(pkg); }}
                            className="text-orange-600 border-orange-300 hover:bg-orange-50"
                          >
                            <Pencil className="h-4 w-4 mr-1" />
                            Modifier
                          </Button>
                        ) : (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => { e.stopPropagation(); setPhotosDialog({ open: true, packageId: pkg.id, oceCode: pkg.oce_code }); }}
                              title="Voir les photos"
                            >
                              <ImageIcon className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => { e.stopPropagation(); printLabel(pkg); }}
                              title="Imprimer étiquette"
                            >
                              <Printer className="h-4 w-4" />
                            </Button>
                            {canDeletePackages && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-orange-600 hover:text-orange-700"
                                onClick={(e) => { e.stopPropagation(); openEditDialog(pkg); }}
                                title="Modifier le colis"
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                            )}
                            {canDeletePackages && !DELETE_BLOCKED_STATUSES.includes(pkg.status) && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-600 hover:text-red-700"
                                onClick={(e) => { e.stopPropagation(); setDeleteReason('duplicate'); setDeleteCustom(''); setDeleteDialog({ open: true, pkg }); }}
                                title="Supprimer le colis"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </>
                        )}
                        {['awaiting_customs', 'arrived'].includes(pkg.status) && pkg.shipment_id ? (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); navigate(`/shipments?status=${pkg.status}`); }}
                            title={language === 'en' ? 'View departure' : 'Voir le départ'}
                            className="cursor-pointer"
                          >
                            <PackageStatusBadge status={pkg.status} size="sm" />
                          </button>
                        ) : (
                          <PackageStatusBadge status={pkg.status} size="sm" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <Pagination
                  currentPage={currentPage}
                  totalPages={Math.ceil(
                    packages.filter(pkg => {
                      if (pkg.status === 'draft') return false;
                      if (statusFilter !== 'all' && pkg.status !== statusFilter) return false;
                      if (!searchQuery) return true;
                      const query = searchQuery.toLowerCase();
                      return (
                        pkg.oce_code?.toLowerCase().includes(query) ||
                        pkg.sender_name?.toLowerCase().includes(query) ||
                        pkg.recipient_name?.toLowerCase().includes(query) ||
                        pkg.sender_phone?.includes(query) ||
                        pkg.recipient_phone?.includes(query)
                      );
                    }).length / itemsPerPage
                  )}
                  itemsPerPage={itemsPerPage}
                  totalItems={packages.filter(pkg => {
                    if (pkg.status === 'draft') return false;
                    if (statusFilter !== 'all' && pkg.status !== statusFilter) return false;
                    if (!searchQuery) return true;
                    const query = searchQuery.toLowerCase();
                    return (
                      pkg.oce_code?.toLowerCase().includes(query) ||
                      pkg.sender_name?.toLowerCase().includes(query) ||
                      pkg.recipient_name?.toLowerCase().includes(query) ||
                      pkg.sender_phone?.includes(query) ||
                      pkg.recipient_phone?.includes(query)
                    );
                  }).length}
                  onPageChange={setCurrentPage}
                  onItemsPerPageChange={(value) => {
                    setItemsPerPage(value);
                    setCurrentPage(1);
                  }}
                />
              </>
            )}
          </CardContent>
        </Card>
        )}

        {/* Audit des notifications (limité à mes colis) */}
        {!isDraftsPage && (
          <NotificationAudit />
        )}

        {/* QR Dialog */}
        {showQRDialog && qrSession && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-card p-4 sm:p-6 rounded-lg max-w-[calc(100vw-2rem)] sm:max-w-lg w-full mx-4">
              <h3 className="text-lg font-semibold mb-4 text-center">📱 QR Code pour Client</h3>
              
              <div className="text-center mb-6">
                {/* QR Code Image */}
                {qrSession.qr_image && (
                  <div className="bg-card p-4 rounded-lg border-2 border-border inline-block mb-4">
                    <img 
                      src={qrSession.qr_image} 
                      alt="QR Code" 
                      className="w-48 h-48 mx-auto"
                    />
                  </div>
                )}
                
                <div className="space-y-2">
                  <p className="text-sm font-medium text-foreground">
                    🎯 Instructions pour le client :
                  </p>
                  <ol className="text-xs text-muted-foreground text-left space-y-1">
                    <li>1. Scannez ce QR code avec votre téléphone</li>
                    <li>2. Remplissez le formulaire avec vos informations</li>
                    <li>3. Attendez la validation du partenaire</li>
                    <li>4. Recevez votre code de suivi par email</li>
                  </ol>
                </div>
                
                <div className="mt-4 p-2 bg-yellow-500/10 rounded border border-yellow-200">
                  <p className="text-xs text-yellow-700">
                    ⏱️ Expire le : {new Date(qrSession.expires_at).toLocaleString('fr-FR')}
                  </p>
                  <p className="text-xs text-yellow-700">
                    🔒 Usage unique - expire après utilisation
                  </p>
                </div>
              </div>
              
              <div className="space-y-3">
                {/* URL de secours */}
                <div className="bg-muted p-3 rounded-lg">
                  <p className="text-xs text-muted-foreground mb-1">Lien de secours (si QR ne fonctionne pas) :</p>
                  <p className="font-mono text-xs break-all text-accent">{qrSession.qr_url}</p>
                </div>
                
                <div className="flex justify-end gap-2">
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      navigator.clipboard.writeText(qrSession.qr_url);
                      setSuccess('Lien copié !');
                    }}
                  >
                    📋 Copier le lien
                  </Button>
                  <Button onClick={() => setShowQRDialog(false)}>
                    {t('close')}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Status updates are now handled by administrators only */}

        {/* Package Photos Dialog */}
        <PackagePhotosDialog
          open={photosDialog.open}
          onOpenChange={(open) => setPhotosDialog(prev => ({ ...prev, open }))}
          packageId={photosDialog.packageId}
          oceCode={photosDialog.oceCode}
        />

        {/* Delete package dialog (admins, avant départ) */}
        <Dialog open={deleteDialog.open} onOpenChange={(open) => { if (!open) setDeleteDialog({ open: false, pkg: null }); }}>
          <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Supprimer le colis {deleteDialog.pkg?.oce_code}</DialogTitle>
              <DialogDescription>
                Réservé aux administrateurs, avant le départ. Suppression logique avec motif tracé.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium">Motif</label>
                <select
                  value={deleteReason}
                  onChange={(e) => setDeleteReason(e.target.value)}
                  className="w-full mt-1 border border-input rounded-md px-3 py-2 text-sm bg-background"
                >
                  {Object.entries(DELETE_REASONS).map(([k, label]) => (
                    <option key={k} value={k}>{label}</option>
                  ))}
                </select>
              </div>
              {deleteReason === 'other' && (
                <textarea
                  value={deleteCustom}
                  onChange={(e) => setDeleteCustom(e.target.value)}
                  placeholder="Précisez le motif…"
                  className="w-full border border-input rounded-md px-3 py-2 text-sm resize-none h-20"
                  maxLength={300}
                />
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteDialog({ open: false, pkg: null })}>Annuler</Button>
              <Button
                className="bg-red-600 hover:bg-red-700 text-white"
                disabled={deleting || (deleteReason === 'other' && !deleteCustom.trim())}
                onClick={handleDeletePackage}
              >
                {deleting ? 'Suppression…' : 'Supprimer'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

      </div>
    </ProfessionalLayout>
  );
};

export default PartnerDashboard;